import {Map, List} from 'immutable';
import _ from 'underscore';
import moment from 'moment-timezone';

import {GET_BET_STATUS, GET_STAKE_STATUS, GET_CASHOUT_STATUS} from 'sagas/actions';

import * as StorageActions from 'actions/Storage';
import * as MakeBetActions from 'actions/MakeBet';
import * as HistoryBetActions from 'actions/HistoryBet';
import * as CashoutActions from 'actions/Cashout';
import * as BigStakeRequestActions from 'actions/BigStakeRequest';
import * as SignoutActions from 'actions/auth/SignoutAction';

import * as BetStatus from 'constants/BetStatus';
import * as StakeStatus from 'constants/StakeStatus';
import * as CashoutStatus from 'constants/CashoutStatus';
import * as CashoutRequestStatus from 'constants/CashoutRequestStatus';
import * as BetCreateErrorStatus from 'constants/BetCreateErrorStatus';
import * as BigStakeCancelError from 'constants/BigStakeCancelError';
import * as BettingState from 'constants/BettingState';

const initialState = new Map();

export default function betHistoryReducer(betHistory, action) {
    switch (action.type) {
        case SignoutActions.SUCCESS_SIGNOUT:
            return initialState;

        case StorageActions.LOAD_STORAGE:
            const storageBetHistory = action.data.betHistory;
            if (storageBetHistory) {
                return storageBetHistory;
            }

            return betHistory;

        case MakeBetActions.MAKE_BET:
            return makeBet(betHistory, action.historyData);

        case MakeBetActions.SUCCESS_MAKE_BET:
            if(action.data.some(b => b.error || b.hasOwnProperty('FreebetErrors'))) {
                return changeBetsToRejected(betHistory, action.data);
            }
            return betCreated(betHistory, action.data);

        case GET_BET_STATUS.SUCCESS:
            return changeBetStatus(betHistory, action.data);

        case GET_BET_STATUS.FAILURE:
            return changeBetsToRejected(betHistory, [action.data]);

        case MakeBetActions.FAILURE_MAKE_BET:
            return changeBetsToRejected(betHistory, action.data);

        case HistoryBetActions.REMOVE_HISTORY_BET:
            return removeHistoryBet(betHistory, action.data);

        case HistoryBetActions.REMOVE_HISTORY_BETS:
            return removeHistoryBets(betHistory);

        case BigStakeRequestActions.SUCCESS_BIG_STAKE_REQUEST_CANCEL:
            return cancelBigStakeRequest(betHistory, action.data);

        case GET_STAKE_STATUS.SUCCESS:
            betHistory = changeStakeBetStatus(betHistory, action.data);
            return updateStakeCashoutStatus(betHistory, action.data);

        case CashoutActions.SET_CASHOUT_STATUS:
            return setCashoutStatus(betHistory, action.data);

        case CashoutActions.SUCCESS_CREATE_CASHOUT:
            betHistory = createCashout(betHistory, action.data);
            return betSold(betHistory, action.data);

        case CashoutActions.FAILURE_CREATE_CASHOUT:
        case GET_CASHOUT_STATUS.FAILURE:
            return clearCashout(betHistory, action.data);

        case GET_CASHOUT_STATUS.SUCCESS:
            return cashoutRequestStateChanged(betHistory, action.data);
    }

    return betHistory;
}

function makeBet(betHistory, bets) {
    _.map(bets, b => {
        betHistory = betHistory.set(b.id, new Map({
            stakeId: undefined,
            stakeTime: undefined,
            cashout: new Map({
                amount: undefined,
                requestId: undefined,
                state: undefined
            }),
            betSlips: b.betSlips,
            betType: b.betType,
            rank: b.rank,
            amount: b.amount,
            odd: b.odd.toFixed(2),
            possibleWinAmount: (b.amount * b.odd).toFixed(2),
            delay: undefined,
            rateChange: undefined,
            betStatus: BetStatus.INIT,
            stakeStatus: StakeStatus.ACTIVE,
            betCreateError: undefined,
            FreebetErrors:undefined,
            resolution: undefined,
            bigStakeCancelError: undefined
        }));
    });

    return betHistory;
}

function betCreated(betHistory, createdBets) {
    for (let i = 0; i < createdBets.length; i++) {
        const createdBet = createdBets[i];
        const id = createdBet.id;

        if (!betHistory.has(id)) {
            continue;
        }

        if (createdBet.error) {
            betHistory = betHistory.updateIn([id], bh => {
                let betErrorStatus = BetCreateErrorStatus.INTERNAL_ERROR;
                let alternativeStake = null;

                switch (createdBet.error) {
                    case -3:
                        betErrorStatus = BetCreateErrorStatus.FACTORS_CHANGED;

                        const factors = createdBet.factors;
                        if (factors) {
                            const rateChange = getRateChange(factors);

                            if (rateChange.length > 0) {
                                bh = bh.set('rateChange', rateChange);
                            }
                        }
                        break;
                    case -4:
                        betErrorStatus = BetCreateErrorStatus.INSUFFICIENT_FUNDS;
                        break;
                    case -7:
                    case -15:
                        betErrorStatus = BetCreateErrorStatus.ACCOUNT_LOCKED;
                        break;
                    case -17:
                        betErrorStatus = BetCreateErrorStatus.SYSTEM_NOT_ALLOWED_FOR_FREEBET;
                        break;

                    case -6:
                        betErrorStatus = BetCreateErrorStatus.MIN_STAKE_AMOUNT;
                        break;
                    case -1:
                    case -2:
                    case -13:
                        betErrorStatus = BetCreateErrorStatus.INCORRECT_STAKE;
                        break;
                    case -5:
                    case -11:
                        betErrorStatus = BetCreateErrorStatus.INCORRECT_VIRTUAL_STAKE;
                        break;
                    case -12:
                        betErrorStatus = BetCreateErrorStatus.FREEBET_BIG_STAKE;
                        break;
                    case -9:
                    case -10:
                        betErrorStatus = BetCreateErrorStatus.INCORRECT_FREEBET_STAKE;
                        break;
                    case -14:
                        betErrorStatus = BetCreateErrorStatus.EXISTING_ACTIVE_STAKE;
                        break;
                    case -16:
                        betErrorStatus = BetCreateErrorStatus.NO_BIG_STAKE_PERMISSION;
                        break;
                    case -18:
                        betErrorStatus = BetCreateErrorStatus.ALTERNATIVE_STAKE;
                        alternativeStake = createdBet.alternativeStake;
                        break;
                    case -19:
                        betErrorStatus = BetCreateErrorStatus.NOT_POSSIBLE_STAKE;
                        break;
                }

                bh = bh.set('betStatus', BetStatus.REJECTED);
                bh = bh.set('alternativeStake', alternativeStake);
                return bh.set('betCreateError', betErrorStatus);
            });
        } else {
            betHistory = betHistory.updateIn([id], bh => {
                const stakeTime = moment.tz(createdBet.stakeTime, 'Asia/Bishkek').clone().tz('UTC');

                const factors = createdBet.factors;
                if (factors) {
                    const rateChange = getRateChange(factors);
                    if (rateChange.length > 0) {
                        bh = bh.set('rateChange', rateChange);
                    }
                }

                bh = bh.set('betStatus', createdBet.state);
                bh = bh.set('stakeId', createdBet.stakeId);
                bh = bh.set('delay', createdBet.delay);
                bh = bh.set('stakeTime', stakeTime);

                return bh;
            });
        }
    }

    return betHistory;
}

function changeBetStatus(betHistory, betStatus) {
    const id = betStatus.id;

    if (!betHistory.has(id)) {
        return betHistory;
    }

    if (betStatus.error) {
        if (betStatus.error === -100) {
            return betHistory;
        }
        return betHistory.updateIn([id], bh => {
            bh = bh.set('betCreateError', BetCreateErrorStatus.INTERNAL_ERROR);
            return bh.set('betStatus', BetStatus.REJECTED);
        });
    }

    const state = betStatus.state;
    const factors = betStatus.factors;
    const resolution = betStatus.resolution;

    return betHistory.updateIn([id], bh => {
        bh = bh.set('betStatus', state);
        bh = bh.set('resolution', resolution);

        if (factors) {
            const rateChange = getRateChange(factors);

            if (rateChange.length > 0) {
                bh = bh.set('rateChange', rateChange);
            }
        }

        return bh;
    });
}

function changeBetsToRejected(betHistory, failedBets) {
    for (let i = 0; i < failedBets.length; i++) {
        const failedBet = failedBets[i];

        betHistory = changeBetToRejected(betHistory, failedBet);
    }

    return betHistory;
}

function changeBetToRejected(betHistory, failedBet) {
    if (betHistory.has(failedBet.id)) {
        betHistory = betHistory.updateIn([failedBet.id], bh => {
            if(failedBet.hasOwnProperty('FreebetErrors')) {
                bh = bh.set('betCreateError', failedBet.FreebetErrors);
            }else {
                bh = bh.set('betCreateError', failedBet.error);
            }
           
            bh = bh.set('betStatus', BetStatus.REJECTED);
            return bh;
        });
    }

    return betHistory;
}

function getRateChange(factors) {
    const rateChange = [];
    for (let i = 0; i < factors.length; i++) {
        const factorId = factors[i].FactorId;
        const factorInitialValue = factors[i].InitialValue;
        const factorNewValue = factors[i].Value;
        const bettingState = factors[i].BettingState;

        switch (bettingState) {
            case BettingState.CORRECT:
                if (factorInitialValue !== factorNewValue) {
                    rateChange.push({
                        factorId,
                        value: factorNewValue
                    });
                }
                break;
            case BettingState.FACTOR_CHANGED:
                rateChange.push({
                    factorId,
                    value: factorNewValue
                });
                break;
            case BettingState.FACTOR_CLOSED:
            case BettingState.OUTCOME_CLOSED:
            case BettingState.EVENT_CLOSED:
                rateChange.push({
                    factorId,
                    unavailable: true
                });
        }
    }

    return rateChange;
}

function removeHistoryBet(betHistory, id) {
    return betHistory.delete(id);
}

function removeHistoryBets(betHistory) {
    const ids = betHistory.keySeq().toArray();

    for (let i = 0; i < ids.length; i++) {
        const id = ids[i];

        const betStatus = betHistory.getIn([id, 'betStatus']);
        if (betStatus &&
            betStatus !== BetStatus.CONFIRMED &&
            betStatus !== BetStatus.CONFIRM_AWAIT &&
            betStatus !== BetStatus.IN_DELAY &&
            betStatus !== BetStatus.STAKE_REQUEST) {
            betHistory = betHistory.delete(id);
        }
    }

    return betHistory;
}

function cancelBigStakeRequest(betHistory, bigStakeRequestCancel) {
    const id = bigStakeRequestCancel.id;
    const error = bigStakeRequestCancel.error;

    if (!betHistory.has(id)) {
        return betHistory;
    }

    if (error !== undefined) {
        if (error === -1) {
            return betHistory.updateIn([id], bh => {
                return bh.set('bigStakeCancelError', BigStakeCancelError.CANCEL_NOT_POSSIBLE);
            });
        } else if (error === -100) {
            return betHistory;
        }
    }

    return betHistory.updateIn([id], bh => {
        return bh.set('betStatus', BetStatus.REJECTED);
    });
}

function changeStakeBetStatus(betHistory, stakeStatus) {
    const id = stakeStatus.id;

    if (!betHistory.has(id)) {
        return betHistory;
    }

    if (stakeStatus.error) {
        if (stakeStatus.error === -2) {
            return betHistory.delete(id);
        }

        return betHistory;
    }

    const state = stakeStatus.state;
    const calculationState = stakeStatus.calculationState;

    return betHistory.updateIn([id], bh => {
        bh = bh.set('betStatus', state);
        return bh.set('stakeStatus', calculationState);
    });
}

function updateStakeCashoutStatus(betHistory, stakeStatus) {
    const id = stakeStatus.id;

    if (!betHistory.has(id)) {
        return betHistory;
    }

    if (stakeStatus.error) {
        return betHistory;
    }

    const cashoutAmount = stakeStatus.cashoutAmount;
    const state = stakeStatus.state;

    switch (state) {
        case BetStatus.CONFIRMED:
            const cashoutStatus = betHistory.getIn([id, 'cashout', 'state']);
            const previousAmount = betHistory.getIn([id, 'cashout', 'amount']);

            if (!cashoutStatus) {
                if (cashoutAmount > 0) {
                    if (!betHistory.hasIn([id, 'cashout'])) {
                        betHistory = betHistory.setIn([id, 'cashout'], new Map());
                    }
                    betHistory = betHistory.updateIn([id, 'cashout'], c => {
                        c = c.set('state', CashoutStatus.AVAILABLE);
                        return c.set('amount', cashoutAmount);
                    });}

                return betHistory;
            }

            switch (cashoutStatus) {
                case CashoutStatus.REJECTED:
                case CashoutStatus.UNAVAILABLE:
                    if (cashoutAmount > 0) {
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('amount', cashoutAmount));
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('state', CashoutStatus.AVAILABLE));
                    }
                    return betHistory;

                case CashoutStatus.AVAILABLE:
                    if (cashoutAmount === 0) {
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('state', CashoutStatus.UNAVAILABLE));
                    } else {
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('amount', cashoutAmount));
                    }
                    return betHistory;

                case CashoutStatus.AMOUNT_CHANGED:
                    if (cashoutAmount === 0) {
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('state', CashoutStatus.UNAVAILABLE));
                    } else {
                        betHistory = betHistory.updateIn([id, 'cashout'],
                                c => c.set('amount', cashoutAmount));
                        if (cashoutAmount === previousAmount) {
                            betHistory = betHistory.updateIn([id, 'cashout'],
                                    c => c.set('state', CashoutStatus.AVAILABLE));
                        }
                    }
                    return betHistory;

                case CashoutStatus.MBOX_SHOWN:
                case CashoutStatus.SELL_REQUESTED:
                case CashoutStatus.SUCCESS:
                    return betHistory;
            }

            return betHistory;

        case BetStatus.CASHOUT:
            const betCashout = betHistory.getIn([id, 'cashout']);
            if (betCashout) {
                return betHistory.updateIn([id, 'cashout'], bh => {
                    return bh.set('state', CashoutStatus.SUCCESS);
                });
            }
    }

    return betHistory;
}

function setCashoutStatus(betHistory, cashout) {
    const betId = cashout.betId;
    const status = cashout.status;

    if (!betHistory.has(betId)) {
        return betHistory;
    }

    return betHistory.updateIn([betId, 'cashout'], c => c.set('state', status));
}

function clearCashout(betHistory, cashout) {
    const betId = cashout.betId;

    if (!betHistory.has(betId)) {
        return betHistory;
    }

    return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.REJECTED));
}

function createCashout(betHistory, createCashout) {
    const betId = createCashout.betId;
    const requestId = createCashout.requestId;
    const amount = createCashout.amount;
    const error = createCashout.error;

    if (!betHistory.has(betId)) {
        return betHistory;
    }

    const betStatus = betHistory.getIn([betId, 'betStatus']);
    if (betStatus !== BetStatus.CONFIRMED) {
        return betHistory;
    }

    if (error !== undefined) {
        if (error === -2) {
            betHistory = betHistory.updateIn([betId, 'cashout'], c => c.set('amount', amount));
            return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.AMOUNT_CHANGED));
        }

        betHistory = betHistory.updateIn([betId, 'cashout'], c => c.set('amount', 0));
        return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.REJECTED));
    }

    if (requestId === -1) {
        return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.SUCCESS));
    }

    betHistory = betHistory.updateIn([betId, 'cashout'], c => c.set('requestId', requestId));
    return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.SELL_REQUESTED));
}

function betSold(betHistory, createCashout) {
    const betId = createCashout.betId;
    const requestId = createCashout.requestId;
    const amount = createCashout.amount;

    if (!betHistory.has(betId)) {
        return betHistory;
    }

    const betStatus = betHistory.getIn([betId, 'betStatus']);
    if (betStatus !== BetStatus.CONFIRMED) {
        return betHistory;
    }

    betHistory = betHistory.updateIn([betId], bh => {
        return bh.set('possibleWinAmount', amount);
    });

    if (requestId === -1) {
        return betHistory.updateIn([betId], c => c.set('betStatus', BetStatus.CASHOUT));
    }

    return betHistory;
}

function cashoutRequestStateChanged(betHistory, cashoutState) {
    const betId = cashoutState.betId;
    const state = cashoutState.state;
    const error = cashoutState.error;

    if (!betHistory.has(betId)) {
        return betHistory;
    }

    const betStatus = betHistory.getIn([betId, 'betStatus']);
    if (betStatus !== BetStatus.CONFIRMED) {
        return betHistory;
    }

    if (error) {
        return betHistory;
    }

    switch (state) {
        case CashoutRequestStatus.ACCEPTED:
            return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.SUCCESS));
        case CashoutRequestStatus.REJECTED:
        case CashoutRequestStatus.TIMEDOUT:
            return betHistory.updateIn([betId, 'cashout'], c => c.set('state', CashoutStatus.REJECTED));

    }

    return betHistory;
}



// WEBPACK FOOTER //
// ./js/sport-betting/reducers/betHistory/betHistoryReducer.js